import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { useModelService, Measure } from 'services/model/model.service';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildDrillDownTableCoreColumns } from 'pages/trading/outlier/DrilldownCoreColumns';
import { DrillDownTableDateGmvValues } from 'pages/trading/outlier/DrillDownTableDateRangeRow';
import { MessageSentiment } from 'pages/trading/model';
import { ShareBar } from 'pages/insights-pro/common/reports/ShareBar';
import { TRADING_ROOT } from 'pages/trading/Trading';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { useCoreDrilldownExportConfigOptions } from 'pages/trading/outlier/DrilldownExportUtil';

export interface CategoryRow extends DrillDownTableDateGmvValues {
  prdCategory: string;
}

const columnHelper = createColumnHelper<CategoryRow>();
const buildColumns = (headers: string[], intl: IntlShape, measure: Measure) => [
  columnHelper.accessor('prdCategory', {
    header: () => headers[0],
    cell: info => (
      <div className="flex gap-2 items-center">
        {findIconForCategory(info.getValue(), 24)}
        <FormattedMessage id={`product.categories.${info.getValue()}`} />
      </div>
    ),
  }),
  columnHelper.accessor(row => row.share, {
    id: 'share',
    header: () => intl.formatMessage({ id: 'trading.drilldown.column.share' }),
    cell: info => <ShareBar width={info.getValue() * 100} color="#314968" />,
  }),
  ...buildDrillDownTableCoreColumns(intl, measure),
];

export interface CategoriesTableProps {
  rows: CategoryRow[];
  measure: Measure;
  sentiment?: MessageSentiment;
  detectionDate?: string;
}

export function CategoriesTable({
  rows,
  measure,
  sentiment = 'NEUTRAL',
  detectionDate = undefined,
}: CategoriesTableProps) {
  const { getAttributes } = useModelService();
  const [prdCategory] = getAttributes(['prdCategory']);
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const onRowClick = (row: Row<unknown>) => {
    const castedRow = row as Row<CategoryRow>;
    navigate(`${TRADING_ROOT}/drilldown/prdCategory/${castedRow.original.prdCategory}${location.search}`);
  };

  const columns = buildColumns([prdCategory.name], intl, measure) as ColumnDef<unknown>[];

  const coreDrilldownExportConfigOptions = useCoreDrilldownExportConfigOptions('categories');

  return (
    <DataTableContextProvider>
      <DataTable
        testId="trading-drilldown-categories-table"
        titleId="insights.category"
        subTitleDecorator={header =>
          `${header}${detectionDate ? ` ${intl.formatMessage({ id: 'trading.drilldown.detection.date' })} ${detectionDate}` : ''}`
        }
        rows={rows}
        columns={columns}
        onRowClick={onRowClick}
        exportConfigOptions={coreDrilldownExportConfigOptions}
        defaultSortingState={[{ id: 'onDayComparisonGmv', desc: sentiment !== 'NEGATIVE' }]}
      />
    </DataTableContextProvider>
  );
}
